import React, { useState } from 'react';

export default function Contact() {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website_url: '', // Honeypot field
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus({ submitting: true, success: false, error: null });

    // Client-side validation
    if (!formData.name || !formData.email || !formData.message) {
      setStatus({
        submitting: false,
        success: false,
        error: 'Udfyld venligst alle påkrævede felter (Navn, Email og Beskrivelse).',
      });
      return;
    }

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          message: formData.message,
          website_url: formData.website_url, // Honeypot
        }),
      });

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({ name: '', phone: '', email: '', message: '', website_url: '' });
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der skete en fejl under afsendelsen. Prøv igen.');
      }
    } catch (err) {
      setStatus({ submitting: false, success: false, error: err.message });
    }
  };

  return (
    <div className="bg-sand-50 text-foreground min-h-screen">
      {/* Header Banner */}
      <section className="relative py-20 md:py-28 bg-[#60b6c0] text-sand-50 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img
            src="https://assets.auxodev.dk/assets/87cec7ac-eba2-4923-aa73-844d6660fb0e/sean-o-406693_2.jpg?width=1920&quality=60&format=auto"
            alt="Book en tid"
            className="h-full w-full object-cover object-center"
          />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center flex flex-col gap-4">
          <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl font-bold tracking-wide leading-tight">
            Book en tid
          </h1>
          <p className="max-w-2xl mx-auto text-sm sm:text-base text-white leading-relaxed">
            Giv mig et kald, send mig en sms, en mail eller udfyld formularen herunder. Jeg glæder mig til at høre fra dig!
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left: Contact Info & Pricing */}
            <div className="lg:col-span-5 flex flex-col gap-8 text-left">
              {/* Info Block */}
              <div className="flex flex-col gap-6">
                <h2 className="font-serif text-2xl font-bold text-black">
                  Kontaktinformation
                </h2>
                <ul className="flex flex-col divide-y divide-sand-200/80 text-sm md:text-base">
                  <li className="flex items-start gap-3 pb-4">
                    <i className="fa-solid fa-user text-black w-5 mt-1" />
                    <div>
                      <span className="font-semibold block text-foreground">Heidi Svelmøe</span>
                      <span className="text-xs text-foreground/60 uppercase tracking-wider">Sangerinde & Sangunderviser</span>
                    </div>
                  </li>
                  <li className="flex items-center gap-3 py-4">
                    <i className="fa-solid fa-phone text-black w-5" />
                    <a href="tel:27794688" className="hover:text-clay-700 transition-colors font-medium">
                      27794688
                    </a>
                  </li>
                  <li className="flex items-center gap-3 py-4">
                    <i className="fa-solid fa-envelope text-black w-5" />
                    <a href="mailto:heidisvelmoe@gmail.com" className="hover:text-clay-700 transition-colors font-medium">
                      heidisvelmoe@gmail.com
                    </a>
                  </li>
                  <li className="flex items-start gap-3 pt-4">
                    <i className="fa-solid fa-location-dot text-black w-5 mt-1" />
                    <div>
                      <span className="font-semibold block text-foreground">Undervisningssted:</span>
                      <span className="text-sm text-foreground/80">
                        Kunst og Filosoficenteret Solvognen (Solsalen)<br />
                        Kronprinsensgade 7b, 5000 Odense C
                      </span>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Pricing & Payments */}
              <div className="bg-white p-6 md:p-8 rounded-3xl border border-sand-200/60 shadow-sm flex flex-col gap-6">
                <h2 className="font-serif text-2xl font-bold text-black">
                  Priser & Betaling
                </h2>
                <div className="flex flex-col gap-4 text-sm">
                  <div className="border-b border-sand-100 pb-3">
                    <span className="font-semibold block text-foreground">Enkelt lektion (60 min)</span>
                    <span className="text-foreground/70">600 kr. (betales ved tilmelding)</span>
                  </div>
                  <div className="border-b border-sand-100 pb-3">
                    <span className="font-semibold block text-foreground text-clay-700">Sangforløb (Anbefales)</span>
                    <span className="text-foreground/70">Køb 8 lektioner til 7 lektioners pris!</span>
                    <span className="font-bold block text-black mt-1">Pris: 4.200 kr. (spar 600 kr.)</span>
                  </div>
                  <div>
                    <span className="font-semibold block text-foreground mb-1">Betalingsmetoder</span>
                    <p className="text-xs text-foreground/70 leading-relaxed">
                      <strong>MobilePay:</strong> 27794688<br />
                      <strong>Bankoverførsel:</strong> Reg: 1684, Kontonr: 0000027626
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-7 flex flex-col gap-8">
              {/* Form */}
              <div className="bg-white p-6 md:p-8 rounded-3xl border border-sand-200/60 shadow-sm text-left">
                <h2 className="font-serif text-2xl md:text-3xl font-bold text-black mb-6">
                  Send en besked
                </h2>

                <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                  {/* Antispam Honeypot Field - ABSOLUTELY HIDDEN */}
                  <div className="sr-only absolute -top-[9999px] -left-[9999px]">
                    <label htmlFor="website_url">Lad dette felt være tomt</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={handleChange}
                      autoComplete="off"
                    />
                  </div>

                  {/* Name */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="name" className="text-xs uppercase tracking-wider font-bold text-foreground/70">
                      Navn <span className="text-clay-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleChange}
                      className="rounded-xl border border-sand-200 bg-sand-50/50 px-4 py-3 text-sm focus:border-clay-500 focus:bg-white focus:outline-none transition-all"
                      placeholder="Dit fulde navn"
                    />
                  </div>

                  {/* Grid for Phone & Email */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    {/* Phone */}
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="phone" className="text-xs uppercase tracking-wider font-bold text-foreground/70">
                        Telefonnummer
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="rounded-xl border border-sand-200 bg-sand-50/50 px-4 py-3 text-sm focus:border-clay-500 focus:bg-white focus:outline-none transition-all"
                        placeholder="Dit telefonnummer"
                      />
                    </div>

                    {/* Email */}
                    <div className="flex flex-col gap-1.5">
                      <label htmlFor="email" className="text-xs uppercase tracking-wider font-bold text-foreground/70">
                        Email <span className="text-clay-600">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        className="rounded-xl border border-sand-200 bg-sand-50/50 px-4 py-3 text-sm focus:border-clay-500 focus:bg-white focus:outline-none transition-all"
                        placeholder="Din emailadresse"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div className="flex flex-col gap-1.5">
                    <label htmlFor="message" className="text-xs uppercase tracking-wider font-bold text-foreground/70">
                      Beskrivelse / Besked <span className="text-clay-600">*</span>
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows="5"
                      value={formData.message}
                      onChange={handleChange}
                      className="rounded-xl border border-sand-200 bg-sand-50/50 px-4 py-3 text-sm focus:border-clay-500 focus:bg-white focus:outline-none transition-all resize-none"
                      placeholder="Skriv lidt om dine ønsker for undervisning eller din forespørgsel..."
                    />
                  </div>

                  {/* Status Messages */}
                  {status.error && (
                    <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm text-red-700 flex items-center gap-2">
                      <i className="fa-solid fa-circle-exclamation text-base" />
                      <span>{status.error}</span>
                    </div>
                  )}

                  {status.success && (
                    <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-800 flex items-center gap-2">
                      <i className="fa-solid fa-circle-check text-base" />
                      <span>Mange tak for din besked! Heidi vender tilbage til dig hurtigst muligt.</span>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={status.submitting}
                    className="mt-2 rounded-full bg-cta hover:bg-cta-hover text-clay-900 py-3.5 px-8 text-xs uppercase tracking-widest font-bold transition-all shadow-md active:scale-98 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {status.submitting ? (
                      <>
                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-clay-900 border-t-transparent" />
                        <span>Sender...</span>
                      </>
                    ) : (
                      <>
                        <span>Send besked</span>
                        <i className="fa-solid fa-paper-plane text-[10px]" />
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Find vej - Maps Section */}
      <section className="py-16 md:py-24 bg-white border-t border-sand-200/60">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-center max-w-2xl mx-auto mb-12 flex flex-col gap-3">
            <h2 className="font-serif text-3xl md:text-4xl font-bold text-black">
              Find vej
            </h2>
            <p className="text-sm sm:text-base text-foreground/70 leading-relaxed">
              Her kan du se, hvor du finder min undervisning i Odense.
            </p>
          </div>

          <div className="max-w-3xl mx-auto">
            {/* Map: Odense */}
            <div className="flex flex-col gap-4">
              <div className="flex items-start gap-3">
                <div>
                  <h3 className="font-serif text-lg font-bold text-black">
                    Undervisningssted (Odense C)
                  </h3>
                  <p className="text-sm text-foreground/70">
                    Kunst og Filosoficenteret Solvognen (Solsalen)<br />
                    Kronprinsensgade 7b, 5000 Odense C
                  </p>
                </div>
              </div>
              <div className="rounded-2xl overflow-hidden shadow-sm border border-sand-200/60 h-[380px] relative">
                <iframe
                  src="https://maps.google.com/maps?q=Kronprinsensgade%207b,%205000%20Odense%20C&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Solsalen, Solvognen Odense"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
